/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { ErrorCodeTypes } from '../enums/ErrorCodeTypes';
import { IErrorAdapter } from '../errormapper/adapters/IErrorAdapter';
import { ErrorCodeSuffixMapper } from './ErrorCodeSuffixMapper';

/**
 * Utility for extracting and formatting error codes from API errors.
 * Uses dependency injection - accepts adapter as parameter.
 */
export class ErrorCodeUtil {
    /**
     * Extracts the sub-error code from an error object.
     * @returns Suffix error code (e.g., 'N603', 'G400') or empty string
     */
    public static extractSubErrorCode(error: any, adapter: IErrorAdapter): string {
        if (!error) {
            return '';
        }

        const normalizedError = adapter.normalizeError(error);
        const modelErrorCode = this.mapErrorToErrorCodeType(normalizedError);

        if (!modelErrorCode) {
            return '';
        }

        return ErrorCodeSuffixMapper.getSuffixCodeFromErrorType(modelErrorCode);
    }

    /**
     * Gets suffix code from ErrorCodeTypes enum value.
     */
    public static getSuffixFromErrorType(errorCodeType: ErrorCodeTypes): string {
        return ErrorCodeSuffixMapper.getSuffixCodeFromErrorType(errorCodeType);
    }

    /**
     * Maps normalized error to ErrorCodeTypes enum.
     *
     * Applies a priority-based mapping strategy:
     * 1. Network errors (error.isNetworkError && error.code) - DNS, connection failures
     * 2. API error codes (error.response.data.code) - Application-specific errors
     * 3. HTTP status codes (error.response.status) - Standard HTTP errors
     *
     * This priority ensures specific error codes take precedence over generic ones.
     *
     * @param error - Normalized error object from IErrorAdapter
     * @returns Mapped ErrorCodeTypes or null if no mapping found
     */
    private static mapErrorToErrorCodeType(error: any): ErrorCodeTypes | null {
        if (!error) {
            return null;
        }

        // Check for network errors using isNetworkError flag
        if (error.isNetworkError && error.code) {
            const networkErrorCode = this.mapNetworkErrorCodeToModelError(error.code);
            if (networkErrorCode) {
                return networkErrorCode;
            }
        }

        // Extract API error code
        let apiErrorCode = '';
        if (error.response?.data?.code) {
            apiErrorCode = error.response.data.code;
        } else if (error.code && !error.isNetworkError) {
            apiErrorCode = error.code;
        }

        // Extract HTTP status
        let httpStatus = 0;
        if (error.response?.status) {
            httpStatus = error.response.status;
        } else if (error.status) {
            httpStatus = error.status;
        }

        // Try API error code mapping
        if (apiErrorCode) {
            const modelError = this.mapApiErrorCodeToModelError(apiErrorCode);
            if (modelError) {
                return modelError;
            }
        }

        // Fallback to HTTP status mapping
        if (httpStatus > 0) {
            return this.mapHttpStatusToModelError(httpStatus);
        }

        return null;
    }

    /**
     * Maps network error codes to ErrorCodeTypes.
     *
     * These are low-level network errors from the HTTP client (e.g., Axios),
     * typically found in error.code when no HTTP response is received.
     *
     * Common sources:
     * - DNS resolution failures (ENOTFOUND)
     * - Connection errors (ECONNREFUSED, ECONNABORTED)
     * - Timeout errors (ETIMEDOUT, ESOCKETTIMEDOUT)
     * - I/O errors (EIO)
     *
     * @param networkErrorCode - The network error code string (e.g., 'ENOTFOUND')
     * @returns Mapped ErrorCodeTypes or null if no mapping exists
     */
    private static mapNetworkErrorCodeToModelError(
        networkErrorCode: string,
    ): ErrorCodeTypes | null {
        const networkErrorMap: Record<string, ErrorCodeTypes> = {
            // DNS/Connection errors
            ENOTFOUND: ErrorCodeTypes.UNKNOWN_SERVER,
            ECONNREFUSED: ErrorCodeTypes.CONNECTION_REFUSED,
            ECONNABORTED: ErrorCodeTypes.CONNECTION_FAILED,
            ERR_NETWORK: ErrorCodeTypes.CONNECTION_FAILED,
            ENETUNREACH: ErrorCodeTypes.CONNECTION_FAILED,

            // Connection state errors
            ECONNRESET: ErrorCodeTypes.CONNECTION_LOST,
            EPIPE: ErrorCodeTypes.CONNECTION_LOST,

            // Timeout errors
            ETIMEDOUT: ErrorCodeTypes.TIMEOUT,
            ESOCKETTIMEDOUT: ErrorCodeTypes.TIMEOUT,
            ERR_SOCKET_TIMEOUT: ErrorCodeTypes.TIMEOUT,

            // Request/Response errors
            ERR_BAD_REQUEST: ErrorCodeTypes.HTTP_BAD_REQUEST,
            ERR_BAD_RESPONSE: ErrorCodeTypes.BAD_RESPONSE,

            // I/O errors
            EIO: ErrorCodeTypes.IO,

            // Generic errors
            ERR_FR_TOO_MANY_REDIRECTS: ErrorCodeTypes.GENERAL,
            ENOTDIR: ErrorCodeTypes.GENERAL,
            EISDIR: ErrorCodeTypes.GENERAL,
        };

        return networkErrorMap[networkErrorCode] || null;
    }

    /**
     * Maps API-specific error codes to ErrorCodeTypes.
     *
     * These are application-level error codes returned by backend services,
     * typically found in error.response.data.code when an HTTP response is received.
     *
     * Error code sources:
     * - VOD/Purchase service: purchaseError, priceMismatch, inSufficientCredit, offerExpired
     * - Jump Channel/Streaming service: invalidChannel, channelNotFound, vodLaunchFailed
     * - Authentication service: unauthorized, notAuthenticated, forbidden
     * - Infrastructure services: apigwError, dynamoDbError, dataNotFound
     *
     * These codes are defined in backend API specifications and documented
     * in errors.xml for reference.
     *
     * @param apiErrorCode - The API error code string (e.g., 'purchaseError')
     * @returns Mapped ErrorCodeTypes or null if no mapping exists
     */
    private static mapApiErrorCodeToModelError(apiErrorCode: string): ErrorCodeTypes | null {
        const errorMap: Record<string, ErrorCodeTypes> = {
            // VOD/Purchase errors
            purchaseError: ErrorCodeTypes.BAD_REQUEST,
            priceMismatch: ErrorCodeTypes.BAD_REQUEST,
            inSufficientCredit: ErrorCodeTypes.FORBIDDEN,
            offerExpired: ErrorCodeTypes.FORBIDDEN,
            rentalNotAllowed: ErrorCodeTypes.FORBIDDEN,

            // Channel/Streaming errors
            invalidChannel: ErrorCodeTypes.BAD_REQUEST,
            channelNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
            serviceUnavailable: ErrorCodeTypes.SERVICE_UNAVAILABLE,
            vodLaunchFailed: ErrorCodeTypes.SERVICE_UNAVAILABLE,
            streamNotAvailable: ErrorCodeTypes.SERVICE_UNAVAILABLE,

            // Authentication/Authorization errors
            unauthorized: ErrorCodeTypes.NOT_AUTHORIZED,
            notAuthenticated: ErrorCodeTypes.CREDENTIAL_NOT_AUTHORIZED,
            forbidden: ErrorCodeTypes.FORBIDDEN,
            accessDenied: ErrorCodeTypes.FORBIDDEN,

            // Generic HTTP errors
            badRequest: ErrorCodeTypes.BAD_REQUEST,
            notFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
            internalError: ErrorCodeTypes.INTERNAL_ERROR,
            timeout: ErrorCodeTypes.TIMEOUT,
            conflict: ErrorCodeTypes.CONFLICT,
            tooManyRequests: ErrorCodeTypes.TOO_MANY_REQUESTS,

            // Content errors
            resourceNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
            contentNotAvailable: ErrorCodeTypes.SERVICE_UNAVAILABLE,
            unsupportedMediaType: ErrorCodeTypes.UNSUPPORTED_MEDIA_TYPE,

            // Infrastructure/Backend errors
            apigwError: ErrorCodeTypes.BAD_GATEWAY,
            dataNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
            deviceInfoNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
            partnerInfoNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
            dynamoDbError: ErrorCodeTypes.INTERNAL_ERROR,
            unexpectedError: ErrorCodeTypes.INTERNAL_ERROR,
            APIGW_ERROR: ErrorCodeTypes.BAD_GATEWAY,
            BAD_REQUEST: ErrorCodeTypes.BAD_REQUEST,
            INTERNAL_SERVER_ERROR: ErrorCodeTypes.INTERNAL_ERROR,
        };

        return errorMap[apiErrorCode] || null;
    }

    /**
     * Maps HTTP status codes to ErrorCodeTypes.
     *
     * Handles standard HTTP status codes from error.response.status.
     * Used as a fallback when no specific API error code is available.
     *
     * Supported ranges:
     * - 4xx: Client errors (400-409, 415, 429)
     * - 5xx: Server errors (500-504)
     * - Unknown 4xx codes default to BAD_REQUEST
     * - Unknown 5xx codes default to INTERNAL_ERROR
     *
     * @param httpStatus - The HTTP status code number (e.g., 404)
     * @returns Mapped ErrorCodeTypes or null if status is not an error code
     */
    private static mapHttpStatusToModelError(httpStatus: number): ErrorCodeTypes | null {
        const statusMap: Record<number, ErrorCodeTypes> = {
            400: ErrorCodeTypes.BAD_REQUEST,
            401: ErrorCodeTypes.NOT_AUTHORIZED,
            403: ErrorCodeTypes.FORBIDDEN,
            404: ErrorCodeTypes.OBJECT_NOT_FOUND,
            405: ErrorCodeTypes.NOT_ALLOWED,
            409: ErrorCodeTypes.CONFLICT,
            415: ErrorCodeTypes.UNSUPPORTED_MEDIA_TYPE,
            429: ErrorCodeTypes.TOO_MANY_REQUESTS,
            500: ErrorCodeTypes.INTERNAL_ERROR,
            502: ErrorCodeTypes.BAD_GATEWAY,
            503: ErrorCodeTypes.SERVICE_UNAVAILABLE,
            504: ErrorCodeTypes.SERVER_TIMEOUT,
        };

        // Direct mapping
        if (statusMap[httpStatus]) {
            return statusMap[httpStatus];
        }

        // Range-based fallback
        if (httpStatus >= 400 && httpStatus < 500) {
            return ErrorCodeTypes.BAD_REQUEST;
        }

        if (httpStatus >= 500) {
            return ErrorCodeTypes.INTERNAL_ERROR;
        }

        return null;
    }
}
